import { useEffect, useLayoutEffect, useState, useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { useParams } from 'react-router-dom'
import { useLazyQuery } from '@apollo/client'
import {
  UserCapturedWinesDocument,
  UserCapturedWinesFilterDocument,
  UserCapturedWinesFiltersDocument,
} from 'api/hooks'
import { getFilterVariables } from 'api/myWines/wines'
import { getSelectedCompanyId } from 'utils/company'
import { ls } from 'utils/my-localstorage'
import { reset, setItems, setPageInfo, getPageInfo } from 'store/captured-wines'
import { reset as databaseReset } from 'store/database'
import {
  CHECKBOX_LIST,
  DATE_INTERVAL,
  filterTypes,
  INTERVAL,
  RADIO_LIST,
  RHEOSTAT,
  RRP_INTERVAL,
  SCORE,
  SEARCH_USERS,
  SWITCHER,
  MULTISELECT_SWITCHER,
} from 'utils/consts'
import { getLocaleId } from 'utils/getLocaleId'
import { useFilterStoreData } from 'utils/hooks/useFilterStoreData'
import {
  useCalculatePageOnPerPageChange,
  usePerPage,
} from 'components/Common/Table/utils'
import { getSelectedCompany } from 'store/me'
import { useAppSelector } from 'store/hooks'
import { FacetInput } from 'api/schemas'
import { getCapturedWinesFiltersCollection } from 'utils/filtersCollections'

// Types
type FilterValue =
  | string[]
  | string
  | number
  | {
      list?: string[]
      switcher?: boolean
      fromDate?: string | null
      toDate?: string | null
      currency?: string[]
      interval?: {
        minValue?: number
        maxValue?: number
      }
    }

interface GetVariablesParams {
  page?: number
  perPage?: number
  query?: string | null
  facetsInputs?: FacetInput[] | null
  onlyShortlist?: boolean
  sort?: string
}

const DEFAULT_SORT = 'UPDATED_AT_DESC'

const getVariables = ({
  page = 1,
  perPage = 20,
  query,
  facetsInputs = [],
  onlyShortlist = false,
}: GetVariablesParams) => {
  return {
    localeId: getLocaleId(),
    companyId: getSelectedCompanyId(),
    page,
    perPage,
    ...(query && { query }),
    filter: {
      ...(facetsInputs && facetsInputs.length > 0 && { facetsInputs }),
      ...(onlyShortlist && { onlyShortlist: true }),
      companyId: getSelectedCompanyId(),
    },
    activeOfferStatusIds: [1],
  }
}

const useGetVariables = () => {
  const { value: perPage } = usePerPage()
  return useCallback(
    (variables: Omit<GetVariablesParams, 'perPage'>) =>
      getVariables({ ...variables, perPage }),
    [perPage]
  )
}

export const mapFilterForQuery = (
  type: string,
  value: FilterValue,
  apiFilterName: string
) => {
  if (!value) return null

  if (type === filterTypes[CHECKBOX_LIST]) {
    if ((Array.isArray(value) && value.length === 0) || value === -1)
      return null
    return {
      listInput: {
        key: apiFilterName,
        byName: value,
      },
    }
  }

  if (type === filterTypes[SEARCH_USERS]) {
    if (Array.isArray(value) && value.length === 0) return null
    return {
      freeListInput: {
        key: apiFilterName,
        byName: value,
      },
    }
  }

  if (type === filterTypes[DATE_INTERVAL]) {
    const dateValue = value as {
      fromDate?: string | null
      toDate?: string | null
    }
    return {
      dateRangeInput: {
        key: apiFilterName,
        fromDate: dateValue?.fromDate ?? null,
        toDate: dateValue?.toDate ?? null,
      },
    }
  }

  if (type === filterTypes[MULTISELECT_SWITCHER]) {
    const complexValue = value as {
      list?: string[]
      switcher?: boolean
      fromDate?: string | null
      toDate?: string | null
    }

    if (complexValue?.list?.length || complexValue?.switcher) {
      return {
        complexInput: {
          key: 'grapeAndMonocepage',
          childrenInputs: [
            {
              booleanInput: {
                key: 'grapeAndMonocepage.monocepage',
                value: complexValue.switcher,
              },
            },
            {
              listInput: {
                key: 'grapeAndMonocepage.grape',
                byName: complexValue.list,
              },
            },
          ],
        },
      }
    }

    if (complexValue?.fromDate || complexValue?.toDate) {
      return {
        dateRangeInput: {
          key: apiFilterName,
          fromDate: complexValue?.fromDate ?? null,
          toDate: complexValue?.toDate ?? null,
        },
      }
    }

    return null
  }

  if (type === filterTypes[RADIO_LIST]) {
    return {
      listInput: {
        key: apiFilterName,
        byName: value,
      },
    }
  }

  if (type === filterTypes[RHEOSTAT] || type === filterTypes[INTERVAL]) {
    return {
      rangeInput: {
        key: apiFilterName,
        ...(value as object),
      },
    }
  }

  if (type === filterTypes[SWITCHER]) {
    return {
      booleanInput: {
        key: apiFilterName,
        value: value,
      },
    }
  }

  if (type === filterTypes[RRP_INTERVAL]) {
    const rrpValue = value as {
      currency?: string[]
      interval?: { minValue?: number; maxValue?: number }
    }
    return {
      complexInput: {
        key: apiFilterName,
        childrenInputs: [
          rrpValue?.currency && {
            listInput: {
              key: `${apiFilterName}.currency`,
              byName: rrpValue.currency,
            },
          },
          rrpValue?.interval && {
            rangeInput: {
              key: `${apiFilterName}.value`,
              minValue: rrpValue.interval.minValue,
              maxValue: rrpValue.interval.maxValue,
            },
          },
        ].filter(Boolean),
      },
    }
  }

  if (type === filterTypes[SCORE]) {
    if (value === -1) return null
    return {
      listInput: {
        key: apiFilterName,
        byName: value,
      },
    }
  }

  return null
}

export const useCapturedWinesFiltersInputs = () => {
  const { storeName, filtersStorageName } = useFilterStoreData()
  const filtersFromStorage = ls.getObject(filtersStorageName)

  const filtersFromStore = useAppSelector(
    state => state?.tableFilters?.[storeName]?.filters
  )

  const getTableFilters = useCallback(
    (filters: Record<string, FilterValue> | null) => {
      if (!filters) return null

      const inputs: any[] = []
      for (const item of getCapturedWinesFiltersCollection()) {
        const { type, filterName, apiFilterName } = item
        const inputItem = mapFilterForQuery(
          type,
          filters[filterName],
          apiFilterName
        )
        if (inputItem) inputs.push(inputItem)
      }

      return inputs.length ? inputs : null
    },
    []
  )

  return { getTableFilters, filtersFromStore, filtersFromStorage }
}

export const usePageData = () => {
  const [_loading, setLoading] = useState(true)
  const dispatch = useDispatch()
  const pageInfo = useAppSelector(getPageInfo)
  const { page = 1 } = pageInfo
  const { sub } = useParams<{ sub?: string }>()
  const isShortlistPage = sub === 'shortlist'
  const getVariablesFunc = useGetVariables()
  const { value: perPage } = usePerPage()
  const calculatePage = useCalculatePageOnPerPageChange()
  const companyCountryId = useAppSelector(getSelectedCompany)?.country?.id

  const { storeName, setPresetActionType } = useFilterStoreData()
  const filtersApplied = useAppSelector(
    state => state?.tableFilters?.[storeName]?.filtersApplied ?? false
  )
  const query = useAppSelector(
    state => state?.tableFilters?.[storeName]?.filters?.query ?? null
  )
  const presetInStore = useAppSelector(
    state => state?.tableFilters?.[storeName]?.preset ?? null
  )

  const { getTableFilters, filtersFromStorage, filtersFromStore } =
    useCapturedWinesFiltersInputs()

  const [getCapturedWines, { called, loading, error, data }] = useLazyQuery(
    UserCapturedWinesDocument,
    {
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
    }
  )

  const getTableData = (vars: { variables?: any } = {}) => {
    getCapturedWines({
      variables: { ...(vars?.variables || {}), companyCountryId },
    })
  }

  const requestTableData = ({ page = 1, fromStore = false }) => {
    getTableData({
      variables: getVariablesFunc({
        page,
        facetsInputs: getTableFilters(
          fromStore ? filtersFromStore : filtersFromStorage
        ),
        query,
        onlyShortlist: isShortlistPage,
      }),
    })
  }

  const requestUpdateFilters = ({ filter = [] }) => {
    getTableData({
      variables: getVariablesFunc({
        page: 1,
        facetsInputs: filter,
        query,
        onlyShortlist: isShortlistPage,
      }),
    })
  }

  const filterQueryData = {
    getFiltersData: UserCapturedWinesFiltersDocument,
    getVariables: variables => getFilterVariables({ ...variables, query }),
    getSpecificFilterData: UserCapturedWinesFilterDocument,
  }

  useLayoutEffect(() => {
    dispatch(databaseReset())
  }, [])

  useEffect(() => {
    if (!data) return

    const productsOrWinesPagination = data?.me?.capturedWinesPagination
    const pageInfo = productsOrWinesPagination?.pageInfo || {}
    const items = productsOrWinesPagination?.items || []
    dispatch(setPageInfo(pageInfo))
    dispatch(setItems(items))
    setLoading(false)
  }, [data])

  useEffect(() => {
    setLoading(loading)
  }, [loading])

  useEffect(() => {
    requestTableData({})
  }, [filtersApplied])

  useEffect(() => {
    if (called) {
      requestTableData({ page })
    }
  }, [called, query, page])

  useEffect(() => {
    const page = calculatePage(pageInfo?.page)
    dispatch(setPageInfo({ ...pageInfo, page }))
    requestTableData({ page })
  }, [perPage])

  useEffect(() => {
    if (!called && !filtersApplied) {
      getTableData({
        variables: getVariablesFunc({
          page: 1,
          sort: DEFAULT_SORT,
          onlyShortlist: isShortlistPage,
        }),
      })
    }
    if (presetInStore && !filtersApplied) {
      dispatch({
        type: setPresetActionType,
        payload: null,
      })
    }
    return () => {
      dispatch(reset())
    }
  }, [])

  useEffect(() => {
    dispatch(setPageInfo({ ...pageInfo, page: 1 }))
    requestTableData({ page: 1 })
  }, [sub])

  return {
    showPreloader: !data || _loading,
    error,
    requestUpdateFilters,
    requestTableData,
    filterQueryData,
    page,
  }
}
